"use client";

import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { loadGisScript } from "./load-gis-script";

/**
 * Minimal subset of the Google Identity Services API the widget uses.
 * Typed inline so we don't take a dependency on `@types/google.accounts`
 * just for two function signatures.
 */
interface GoogleCredentialResponse {
  credential: string;
  select_by?: string;
  clientId?: string;
}
interface GoogleIdConfiguration {
  client_id: string;
  callback: (resp: GoogleCredentialResponse) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
  ux_mode?: "popup" | "redirect";
}
interface GoogleButtonOptions {
  type?: "standard" | "icon";
  theme?: "outline" | "filled_blue" | "filled_black";
  size?: "large" | "medium" | "small";
  text?: "signin_with" | "signup_with" | "continue_with" | "signin";
  shape?: "rectangular" | "pill" | "circle" | "square";
  width?: string | number;
}
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: GoogleIdConfiguration) => void;
          renderButton: (parent: HTMLElement, options: GoogleButtonOptions) => void;
          prompt: () => void;
          cancel: () => void;
        };
      };
    };
  }
}

interface GoogleSignInButtonProps {
  /** Operator's Google OAuth client_id — PUBLIC by design (same one
   *  the operator embeds on their main-site Google login). The BE
   *  verifies `id_token.aud === clientId` on `startIdentifiedVisitor`,
   *  so a leaked clientId can't be abused (Google's allowlist + BE
   *  audience check are the actual security boundaries). */
  clientId: string;
  /** Called with the Google `id_token` when the visitor completes the
   *  popup. Caller routes it through `Wexio('identify',
   *  { googleIdToken })` → `startIdentifiedVisitor`. */
  onCredential: (idToken: string) => void;
}

/**
 * "Sign in with Google" — custom-styled button that triggers the
 * native Google Identity Services popup under the hood.
 *
 * We can't render a TRULY-custom OAuth flow for ID Tokens (Google
 * doesn't expose `initTokenClient` for id_tokens — only access
 * tokens), so the standard pattern is:
 *
 *   1. Initialise GIS with the operator's `clientId` + a callback.
 *   2. Render Google's own `renderButton` into an OFFSCREEN mount.
 *      Visitor never sees it; the DOM node + handlers exist.
 *   3. Our visible button (styled to match the passkey button so the
 *      auth gate looks coherent) `click()`s the offscreen GIS button
 *      programmatically. GIS runs its full popup flow → id_token
 *      back through our callback.
 *
 * Gives us full visual control over the gate while keeping all the
 * GIS auth UX (popup, account chooser, FedCM, cancellation) intact.
 * Lazy-loads `accounts.google.com/gsi/client` on first mount so
 * non-Google-enabled embeds pay zero network cost.
 */
export function GoogleSignInButton({ clientId, onCredential }: GoogleSignInButtonProps) {
  const t = useTranslations("messages");
  const hiddenMountRef = useRef<HTMLDivElement | null>(null);
  // `loading` until the GIS script is usable, `error` when it cannot load (ad blocker, offline) or
  // produced nothing to click. The button never silently does nothing.
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  // Bumped by a tap after a failure to load the script again.
  const [attempt, setAttempt] = useState(0);
  // Latest callback in a ref so the GIS initialize closure stays
  // wired to the CURRENT onCredential even if the parent re-renders.
  const onCredentialRef = useRef(onCredential);
  useEffect(() => {
    onCredentialRef.current = onCredential;
  }, [onCredential]);

  // `attempt` is a deliberate re-run trigger (retry after a failed load), not read in the body.
  // biome-ignore lint/correctness/useExhaustiveDependencies: attempt re-runs the load
  useEffect(() => {
    if (typeof window === "undefined") return;
    let cancelled = false;
    setStatus("loading");

    const initAndRender = () => {
      const g = window.google?.accounts?.id;
      const mount = hiddenMountRef.current;
      if (!g || !mount) {
        setStatus("error");
        return;
      }
      g.initialize({
        client_id: clientId,
        callback: (resp) => {
          if (resp?.credential) onCredentialRef.current(resp.credential);
        },
        auto_select: false,
        cancel_on_tap_outside: true,
        ux_mode: "popup",
      });
      // Clear any prior render (clientId change across HMR cycles).
      mount.replaceChildren();
      g.renderButton(mount, {
        type: "standard",
        theme: "outline",
        size: "large",
        // Width is required for renderButton to actually emit the
        // inner div[role="button"] our click-proxy targets below.
        width: 200,
      });
      setStatus("ready");
    };

    loadGisScript().then(
      () => {
        if (!cancelled) initAndRender();
      },
      () => {
        if (!cancelled) setStatus("error");
      },
    );
    return () => {
      cancelled = true;
    };
  }, [clientId, attempt]);

  const handleClick = () => {
    if (status === "error") {
      setAttempt((n) => n + 1);
      return;
    }
    const mount = hiddenMountRef.current;
    if (!mount) return;
    // GIS renders an interactive div[role="button"] inside the mount.
    // Proxy our visible-button click to it so the visitor sees our
    // brand-tokenised UI while Google's popup flow runs as it would
    // from its own button.
    const gisButton = mount.querySelector<HTMLElement>('div[role="button"]');
    if (!gisButton) {
      setStatus("error");
      return;
    }
    gisButton.click();
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={status === "loading"}
        aria-busy={status === "loading"}
        className="flex w-full items-center justify-center gap-2 rounded-wx border border-wx-border bg-wx-bg-elevated px-4 py-2 text-sm font-medium text-wx-fg transition-colors hover:bg-wx-bg-elevated-2 disabled:opacity-60"
      >
        <GoogleGlyph />
        <span>{t("googleSignIn")}</span>
      </button>
      {status === "error" && (
        <p role="alert" className="text-center text-xs text-wx-danger">
          {t("googleUnavailable")}
        </p>
      )}
      {/* Offscreen mount for the real GIS button — kept out of the
          accessibility tree and the layout flow. Visually-hidden
          pattern (not `display:none`, which prevents GIS from
          measuring + rendering). */}
      <div
        ref={hiddenMountRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          clipPath: "inset(50%)",
          whiteSpace: "nowrap",
        }}
      />
    </>
  );
}

/** Inline SVG of Google's "G" mark — keeps the button self-contained
 *  (no extra image fetch, no bundle hit beyond a handful of paths). */
function GoogleGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
      <path
        d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.614z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </svg>
  );
}
